import { bodyLock, bodyUnlock } from '../helpers/bodyLock';

interface MenuSection {
    trigger: HTMLButtonElement;
    panel: HTMLElement;
    tabbableChildren: Array<HTMLButtonElement | HTMLAnchorElement>;
}

const navBreakpoint = 992;

class MobileNavigation {
    container: HTMLElement;
    inner: HTMLElement;
    menuSections: Array<MenuSection>;
    mainPanel: HTMLElement;
    toggleButtons: Array<HTMLButtonElement>;
    isOpen: boolean;
    activePanels: Array<HTMLElement>;
    bodyStyles: string;

    constructor(mobileNavContainer: HTMLElement) {
        this.container = mobileNavContainer;
        this.inner = this.container.querySelector('div') as HTMLElement;
        this.menuSections = [];
        this.activePanels = [];
        this.mainPanel = this.container.querySelector('[data-mobile-nav-panel]') as HTMLElement;
        this.toggleButtons = [].slice.call(this.container.querySelectorAll('[data-mobile-nav-toggle]')) as Array<HTMLButtonElement>;
        this.isOpen = false;
        this.checkEmptyLinks()
    }

    init() {
        this.setAriaCurrentToNav();
        this.toggleButtons.forEach(button => {
            button.addEventListener('click', () => {
                this.toggleMainPanel();
            });
        });

        const triggers = [].slice.call(this.container.querySelectorAll('[aria-haspopup]')) as Array<HTMLButtonElement>;
        triggers.forEach(trigger => {
            const triggerId = trigger.getAttribute('aria-controls');
            const panel = this.container.querySelector(`#${triggerId}`) as HTMLElement;
            if (trigger && panel) {
                const tabbableChildren = [].slice.call(panel.querySelectorAll(`[data-owned-by="${triggerId}"]`)) as Array<HTMLButtonElement | HTMLAnchorElement>;
                this.menuSections.push({
                    trigger: trigger,
                    panel: panel,
                    tabbableChildren: tabbableChildren
                });
            }
        });

        this.menuSections.forEach(section => {
            this.setChildTabindexes(section, -1);
            section.trigger.addEventListener('click', () => {
                this.togglePanel(section);
            });
            const closeBtn = section.panel.querySelector('[aria-controls]') as HTMLButtonElement;
            if (closeBtn) {
                closeBtn.addEventListener('click', () => {
                    this.closePanel(section);
                });
            }
        });

        document.body.addEventListener('keyup', (e: KeyboardEvent) => {
            if (e.which == 27) {
                this.closeMainPanel();
            }
        });
    }

    toggleMainPanel() {
        if (!this.isOpen) {
            this.openMainPanel();
        }
        else {
            this.closeMainPanel();
        }
    }

    openMainPanel() {
        if (!this.isOpen) {
            this.toggleButtons[0].setAttribute('aria-expanded', 'true');
            this.mainPanel.setAttribute('aria-hidden', 'false');
            this.bodyStyles = bodyLock();
            this.container.classList.add('is-open');
            this.isOpen = true;
            this.activePanels = [this.container];
        }
    }

    closeMainPanel() {
        if (this.isOpen) {
            this.toggleButtons[0].setAttribute('aria-expanded', 'false');
            this.mainPanel.setAttribute('aria-hidden', 'true');
            bodyUnlock(this.bodyStyles);
            this.container.classList.remove('is-open');
            this.closeAllPanels();
            this.inner.style.maxHeight = '';
            this.isOpen = false;
        }
    }

    togglePanel(section: MenuSection) {
        if (section.trigger && section.panel) {
            if (section.trigger.getAttribute('aria-expanded') == 'true') {
                this.closePanel(section);
            }
            else {
                this.openPanel(section);
            }
        }
    }

    openPanel(section: MenuSection) {
        section.trigger.setAttribute('aria-expanded', 'true');
        section.panel.setAttribute('aria-hidden', 'false');
        this.setChildTabindexes(section, 0);
        this.activePanels.push(section.panel);
        this.handleParentOverflow();
    }

    closePanel(section: MenuSection) {
        section.trigger.setAttribute('aria-expanded', 'false');
        section.panel.setAttribute('aria-hidden', 'true');
        this.setChildTabindexes(section, -1);
        this.activePanels = this.activePanels.filter(item => {
            return item != section.panel;
        });
        this.handleParentOverflow();
    }

    closeAllPanels() {
        this.menuSections.forEach(sectionInList => {
            this.closePanel(sectionInList);
        });
    }

    setChildTabindexes(section: MenuSection, tabIndexValue: number) {
        section.tabbableChildren.forEach(item => {
            item.tabIndex = tabIndexValue;
        });
    }

    handleParentOverflow() {
        const parentSections = this.activePanels.slice(0, this.activePanels.length - 1);
        parentSections.forEach(panel => {
            panel.style.overflow = 'hidden';
        });
        const inactiveSections = this.menuSections.filter(section => {
            return !parentSections.includes(section.panel);
        });
        inactiveSections.forEach(section => {
            section.panel.style.overflow = '';
        });
        if (this.activePanels.length == 1) {
            this.container.style.overflow = '';
        }
    }

    testBreakpoint() {
        if (window.innerWidth > navBreakpoint && this.isOpen) {
            this.closeMainPanel();
        }
    }

    setAriaCurrentToNav() {
        const pathArray = window.location.pathname.split('/').filter(e => e.length > 0);
        const urlThree = [pathArray[0]];
        if (pathArray.length) {
            pathArray.reduce((previousValue, currentValue) => {
                const toArray = `${previousValue}/${currentValue}`;
                urlThree.push(toArray);
                return toArray;
            })
            const navs = document.getElementsByClassName('nav-main-A-list-item-link mobile');
            if (navs.length) {
                for (let i = 0; i < navs.length; i++) {
                    const href = navs[i].getAttribute('href').slice(1, -1);
                    if (urlThree.some(el => el === href)) {
                        if (!navs[i].hasAttribute('aria-current') && !navs[i].classList.contains('nav-main-A-panel-heading-link')) {
                            navs[i].setAttribute('aria-current', 'true');
                        }
                    }
                }
            }
        }
    }
    checkEmptyLinks(): void {
        document.addEventListener("DOMContentLoaded", () => {
            const nav = document.getElementById('Nav_mobile_main');
            if (!nav) return;

            const emptyLinks: NodeListOf<HTMLAnchorElement> = nav.querySelectorAll('a[href=""]');

            emptyLinks.forEach(link => {
                link.addEventListener('click', (event: MouseEvent) => {
                    event.preventDefault();

                    const parentLi = link.closest('li');
                    if (!parentLi) return;

                    const toggleButton = parentLi.querySelector<HTMLButtonElement>('button[role="menuitem"]');
                    if (toggleButton) {
                        const control = toggleButton.getAttribute('aria-controls');
                        const targetPanel = this.menuSections.find(item => item.panel.id === control);

                        if (targetPanel) {
                            this.togglePanel(targetPanel);
                        }
                    }
                });
            });
        });
    }
}

let MobileNav: MobileNavigation;
const mobileNavContainer = document.querySelector('#Nav_mobile') as HTMLElement;
if (mobileNavContainer) {
    MobileNav = new MobileNavigation(mobileNavContainer);
}

export { MobileNav };